import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { PLATFORM_META, POST_FORMATS, imageSrc } from "../lib/api";
import { Sparkles, Image as ImageIcon, Loader2, Send, Save, Wand2, Layers, Zap, Clock } from "lucide-react";
import { toast } from "sonner";

const TONE_LIST = ["Professional", "Casual", "Storytelling", "Viral Hook"];
const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";
const FORMAT_ICON = { static: ImageIcon, viral: Zap, carousel: Layers };

export const PostComposer = ({ onDone, resumeId, seed }) => {
  const [fmt, setFmt] = useState("static");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Professional");
  const [brandId, setBrandId] = useState("");
  const [brands, setBrands] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [platforms, setPlatforms] = useState(["linkedin"]);
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [slides, setSlides] = useState([]);
  const [slidesCount, setSlidesCount] = useState(4);
  const [scheduledAt, setScheduledAt] = useState("");
  const [genCap, setGenCap] = useState(false);
  const [genImg, setGenImg] = useState(false);
  const [genCar, setGenCar] = useState(false);
  const [slideBusy, setSlideBusy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  const [autoMode, setAutoMode] = useState(true);
  const [lastAuto, setLastAuto] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [progress, setProgress] = useState(null);   // {done, failed, total, current}
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  }, []);

  // derive platforms from selected accounts
  useEffect(() => {
    if (accountIds.length) {
      const plats = [...new Set(accounts.filter((a) => accountIds.includes(a.id)).map((a) => a.platform))];
      setPlatforms(plats);
    }
  }, [accountIds, accounts]);

  const toggleAccount = (id) => setAccountIds((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const togglePlatform = (p) => setPlatforms((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));

  const generateCaption = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first");
    setGenCap(true);
    try {
      const { data } = await api.post("/generate/caption", { topic, tone, platforms, brand_id: brandId || null, fmt });
      setCaption(data.caption); setHashtags(data.hashtags || []);
      if (!imagePrompt) setImagePrompt(topic);
      toast.success("Caption written by ScaleMax");
    } catch (e) { toast.error(e?.response?.data?.detail || "Caption generation failed", { duration: 10000 }); } finally { setGenCap(false); }
  };

  const generateImage = async () => {
    const p = imagePrompt || topic;
    if (!p.trim()) return toast.error("Enter an image prompt or topic");
    setGenImg(true);
    try {
      const { data } = await api.post("/generate/image", {
        prompt: p, brand_id: brandId || null, fmt, topic, caption,
      });
      setImageB64(data.image_base64); toast.success("Image generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image generation failed", { duration: 10000 }); } finally { setGenImg(false); }
  };

  // Planning the slides and rendering their images are one action - splitting
  // them meant pressing generate on every slide by hand.
  const generateCarousel = async () => {
    if (!topic.trim()) return toast.error("Enter a topic first");
    setGenCar(true);
    try {
      await generateEverything();
    } finally { setGenCar(false); }
  };

  const genSlideImage = async (idx) => {
    setSlideBusy(idx);
    try {
      const sl = slides[idx];
      const { data } = await api.post("/generate/image", {
        prompt: sl.image_prompt || sl.heading || topic, brand_id: brandId || null,
        fmt: "carousel", directed: Boolean(sl.image_prompt),
      });
      const next = slides.map((x, i) => (i === idx ? { ...x, image_base64: data.image_base64, image_status: "ready" } : x));
      setSlides(next);
      if (draftId) await api.put(`/posts/${draftId}`, { slides: next });
      toast.success(`Slide ${idx + 1} image ready`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Slide image failed", { duration: 10000 }); } finally { setSlideBusy(null); }
  };

  const updateSlide = (idx, k, v) => setSlides((cur) => cur.map((x, i) => (i === idx ? { ...x, [k]: v } : x)));

  const draftPayload = () => ({
    topic, caption, hashtags, fmt, slides, platforms,
    account_ids: accountIds, brand_id: brandId || null, tone,
    image_prompt: imagePrompt || topic,
    ...(fmt !== "carousel" && imageB64 ? { image_base64: imageB64 } : {}),
    ...(scheduledAt ? { scheduled_at: new Date(scheduledAt).toISOString() } : {}),
  });

  /* Saves the work in progress so it survives leaving the page, and returns
     the post id the background image job will run against. */
  const persistDraft = async (overrides = {}) => {
    const payload = { ...draftPayload(), ...overrides };
    if (draftId) {
      await api.put(`/posts/${draftId}`, payload);
      return draftId;
    }
    const { data } = await api.post("/posts", payload);
    setDraftId(data.id);
    try { localStorage.setItem("autocruise_draft", data.id); } catch (e) { /* private mode */ }
    return data.id;
  };

  /* The images are rendered by the server, one slide at a time, so the work
     continues if you navigate away or close the tab. This only watches. */
  const watchProgress = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/posts/${id}`);
        if (data.slides?.length) setSlides(data.slides);
        setProgress(data.image_progress || null);
        if (["ready", "failed", "partial"].includes(data.image_status)) {
          clearInterval(pollRef.current);
          setProgress(null);
          setAuto(false);
          const failed = (data.slides || []).filter((x) => x.image_status === "failed");
          if (failed.length) {
            toast.error(failed[0].image_error || `${failed.length} slide image(s) failed`, { duration: 12000 });
          } else {
            toast.success("All slide images ready");
          }
        }
      } catch (e) { /* keep polling; a blip should not stop the watch */ }
    }, 2500);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    if (!seed) return;
    setFmt(seed.fmt || "viral");
    setTopic(seed.topic || "");
    setCaption(seed.caption || "");
    setHashtags(seed.hashtags || []);
    setImagePrompt(seed.image_prompt || "");
    setSlides([]);
    setImageB64(null);
    setDraftId(null);
    setLastAuto(seed.topic || "");   // the copy is already written; do not redo it
    setAutoMode(false);
    (async () => {
      setGenImg(true);
      try {
        const { data } = await api.post("/generate/image", {
          prompt: seed.image_prompt, brand_id: seed.brand_id || null,
          fmt: seed.fmt || "viral", directed: true,
        });
        setImageB64(data.image_base64);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Image generation failed", { duration: 12000 });
      } finally { setGenImg(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  /* Coming back to the composer picks up where you left off. */
  useEffect(() => {
    if (seed) return;
    if (resumeId) { loadDraft(resumeId); return; }
    let saved = null;
    try { saved = localStorage.getItem("autocruise_draft"); } catch (e) { /* ignore */ }
    if (saved) loadDraft(saved, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, seed]);

  const loadDraft = async (id, quiet = false) => {
    try {
      const { data } = await api.get(`/posts/${id}`);
      if (data.status !== "draft") {
        try { localStorage.removeItem("autocruise_draft"); } catch (e) { /* ignore */ }
        return;
      }
      setDraftId(data.id);
      setFmt(data.fmt || "static");
      setTopic(data.topic || "");
      setCaption(data.caption || "");
      setHashtags(data.hashtags || []);
      setSlides(data.slides || []);
      setPlatforms(data.platforms || ["linkedin"]);
      setAccountIds(data.account_ids || []);
      setBrandId(data.brand_id || "");
      setTone(data.tone || "Professional");
      setImagePrompt(data.image_prompt || "");
      setLastAuto(data.topic || "");   // do not re-trigger auto-generation
      setProgress(data.image_progress || null);
      if (data.image_status === "generating" || data.image_status === "pending") {
        setAuto(true);
        watchProgress(data.id);
      }
      if (!quiet) toast.success("Draft loaded");
    } catch (e) {
      try { localStorage.removeItem("autocruise_draft"); } catch (err) { /* ignore */ }
    }
  };

  /* Entering a topic used to be the first of several manual steps: write the
     caption, then plan the slides, then press generate on each image. This
     runs the whole chain from the topic alone. */
  const generateEverything = async () => {
    if (!topic.trim()) return;
    setAuto(true);
    try {
      if (fmt === "carousel") {
        const { data } = await api.post("/generate/carousel", {
          topic, slides_count: Number(slidesCount), tone, platforms, brand_id: brandId || null,
        });
        setSlides(data.slides);
        setCaption((c) => c || data.slides.map((x) => x.heading).filter(Boolean).join(" • "));
        toast.success(`Drafted ${data.slides.length} slides — rendering images in the background`);

        // Save first, then let the server render the slides one at a time. The
        // job lives on the server, so leaving this page does not cancel it and
        // you can start the next post while it finishes.
        const id = await persistDraft({ slides: data.slides, fmt: "carousel" });
        await api.post(`/posts/${id}/generate-images`);
        watchProgress(id);
      } else {
        const cap = await api.post("/generate/caption", {
          topic, tone, platforms, brand_id: brandId || null, fmt,
        });
        setCaption(cap.data.caption);
        setHashtags(cap.data.hashtags || []);
        const img = await api.post("/generate/image", {
          prompt: imagePrompt || topic, brand_id: brandId || null, fmt,
          topic, caption: cap.data.caption,
        });
        setImageB64(img.data.image_base64);
        setAuto(false);
        toast.success("Caption and image ready");
      }
    } catch (e) {
      setAuto(false);
      toast.error(e?.response?.data?.detail || "Generation failed", { duration: 12000 });
    }
  };

  /* Debounced so it fires once the topic has settled, not on every keystroke.
     Only ever runs for a topic it has not already generated for. */
  useEffect(() => {
    if (!autoMode || !topic.trim() || topic.trim() === lastAuto) return undefined;
    const timer = setTimeout(() => {
      setLastAuto(topic.trim());
      generateEverything();
    }, 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic, fmt, autoMode]);

  const bestTime = async () => {
    try {
      const { data } = await api.get(`/best-time/next?platform=${platforms[0] || "linkedin"}&index=0`);
      const dt = new Date(data.scheduled_at);
      const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setScheduledAt(local);
      toast.success(`Best time picked (engagement score ${data.score})`);
    } catch { toast.error("Could not fetch best time"); }
  };

  const validate = () => {
    if (!topic.trim()) { toast.error("Topic is required"); return false; }
    if (!caption.trim()) { toast.error("Generate or write a caption"); return false; }
    if (!platforms.length) { toast.error("Pick a platform or account"); return false; }
    if (fmt === "carousel" && slides.length === 0) { toast.error("Generate carousel slides"); return false; }
    return true;
  };

  const clearDraftMemory = () => {
    try { localStorage.removeItem("autocruise_draft"); } catch (e) { /* ignore */ }
    setDraftId(null);
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setBusy(true);
    // Updates the draft already in progress rather than creating a second copy
    // of the same post.
    try {
      await persistDraft();
      toast.success("Saved to drafts");
      clearDraftMemory();
      onDone && onDone("drafts");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const submitApproval = async () => {
    if (!validate()) return;
    if (progress && progress.total) {
      return toast.error("Images are still rendering — submit once they finish.");
    }
    setBusy(true);
    try {
      const id = await persistDraft();
      const { data: res } = await api.post(`/posts/${id}/submit`);
      const sent = Object.entries(res.delivery || {}).filter(([, v]) => v.sent).map(([k]) => k);
      toast.success(sent.length ? `Sent for approval via ${sent.join(", ")}` : "Sent to approval queue");
      clearDraftMemory();
      onDone && onDone("approvals");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const FmtIcon = FORMAT_ICON[fmt];

  return (
    <div className="space-y-6" data-testid="composer-view">
      <div>
        <p className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-2">AI Composer</p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-50">Create a post</h1>
      </div>

      {/* Format selector */}
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(POST_FORMATS).map(([k, m]) => {
          const Icon = FORMAT_ICON[k];
          const on = fmt === k;
          return (
            <button key={k} data-testid={`format-${k}`} onClick={() => setFmt(k)}
              className={`glass rounded-2xl p-4 text-left transition-all ${on ? "ring-1 ring-indigo-500/60 shadow-[0_0_20px_rgba(99,102,241,0.15)]" : "hover:border-slate-700"}`}>
              <Icon className={`w-5 h-5 mb-2 ${on ? "text-indigo-300" : "text-slate-500"}`} />
              <p className="text-sm font-semibold text-slate-100">{m.label}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{m.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left controls */}
        <div className="lg:col-span-3 space-y-5">
          <div className="glass rounded-2xl p-6 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Brand voice</label>
                <select data-testid="composer-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)} className={inputCls}>
                  <option value="">No brand</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Tone</label>
                <select data-testid="composer-tone" value={tone} onChange={(e) => setTone(e.target.value)} className={inputCls}>
                  {TONE_LIST.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-slate-400">Topic / Idea</label>
                <button
                  type="button"
                  data-testid="composer-auto-toggle"
                  onClick={() => setAutoMode((v) => !v)}
                  className={`flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full transition-colors ${
                    autoMode
                      ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
                      : "bg-slate-800/60 text-slate-500 border border-slate-700/60"
                  }`}
                  title="Write the caption and render the images automatically once you stop typing"
                >
                  <Wand2 className="w-3 h-3" />
                  Auto-generate {autoMode ? "on" : "off"}
                </button>
              </div>
              <input data-testid="composer-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Launching our new analytics dashboard" className={inputCls} />
              {auto && (
                <p className="flex items-center gap-2 text-[11px] text-indigo-300 mt-2" data-testid="composer-auto-status">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {fmt === "carousel"
                    ? "Planning slides and rendering each image in one consistent style…"
                    : "Writing the caption and rendering the image…"}
                </p>
              )}
              {autoMode && !auto && !topic.trim() && (
                <p className="text-[11px] text-slate-600 mt-2">
                  Type a topic — the caption and {fmt === "carousel" ? "every slide image" : "image"} are generated for you.
                </p>
              )}
            </div>

            {/* Accounts */}
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Post to accounts</label>
              {accounts.length ? (
                <div className="flex flex-wrap gap-2">
                  {accounts.map((a) => {
                    const on = accountIds.includes(a.id);
                    return (
                      <button key={a.id} data-testid={`account-chip-${a.id}`} onClick={() => toggleAccount(a.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${on ? "text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                        style={on ? { background: `${PLATFORM_META[a.platform].color}22`, borderColor: PLATFORM_META[a.platform].color, color: "#fff" } : {}}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: PLATFORM_META[a.platform].color }} />
                        {a.display_name}{a.account_kind === "page" && <span className="ml-1 text-[9px] px-1 rounded bg-sky-500/15 text-sky-300">Page</span>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <p className="text-xs text-slate-500 mb-2">No accounts connected — pick platforms directly:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PLATFORM_META).map(([key, meta]) => {
                      const on = platforms.includes(key);
                      return (
                        <button key={key} data-testid={`platform-${key}`} onClick={() => togglePlatform(key)}
                          className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-colors ${on ? "text-white" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                          style={on ? { background: `${meta.color}22`, borderColor: meta.color, color: "#fff" } : {}}>{meta.label}</button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Schedule (optional)</label>
              <div className="flex gap-2">
                <input data-testid="composer-schedule" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={inputCls} />
                <button data-testid="best-time-btn" onClick={bestTime} className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-indigo-300 text-xs font-semibold transition-colors">
                  <Clock className="w-3.5 h-3.5" /> Best time
                </button>
              </div>
            </div>
          </div>

          {/* Caption */}
          <div className="glass rounded-2xl p-6 space-y-4">
            <button data-testid="generate-caption-btn" onClick={generateCaption} disabled={genCap}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold transition-colors disabled:opacity-60">
              {genCap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 text-indigo-300" />}
              {genCap ? "Writing with ScaleMax..." : `Generate ${fmt === "viral" ? "viral " : ""}caption`}
            </button>
            <textarea data-testid="composer-caption" value={caption} onChange={(e) => setCaption(e.target.value)} rows={5}
              placeholder="Your caption will appear here..." className={`${inputCls} resize-none`} />
            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">{hashtags.map((h) => <span key={h} className="text-[11px] font-mono text-indigo-300">#{h}</span>)}</div>
            )}
          </div>

          {/* Static/Viral image OR Carousel slides */}
          {fmt !== "carousel" ? (
            <div className="glass rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2"><ImageIcon className="w-4 h-4 text-indigo-300" /><h3 className="font-heading font-bold text-slate-100">AI Image</h3></div>
              <input data-testid="composer-image-prompt" value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="Describe the visual (defaults to topic)" className={inputCls} />
              <button data-testid="generate-image-btn" onClick={generateImage} disabled={genImg}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
                {genImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {genImg ? "Generating (~up to 1 min)..." : "Generate image"}
              </button>
            </div>
          ) : (
            <div className="glass rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-300" /><h3 className="font-heading font-bold text-slate-100">Carousel slides</h3></div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-400">Slides</label>
                  <input data-testid="slides-count" type="number" min={2} max={15} value={slidesCount} onChange={(e) => setSlidesCount(e.target.value)} className="w-14 bg-slate-950/60 border border-slate-800 rounded-lg px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-indigo-500/60" />
                </div>
              </div>
              <button data-testid="generate-carousel-btn" onClick={generateCarousel} disabled={genCar}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold transition-colors disabled:opacity-60">
                {genCar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {genCar ? "Designing slides..." : "Generate carousel"}
              </button>
              {progress && progress.total > 0 && (
                <div data-testid="carousel-progress" className="rounded-xl bg-slate-950/60 border border-slate-800 p-3">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-slate-300 font-semibold flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-300" />
                      Rendering slide {Math.min((progress.current ?? 0) + 1, progress.total)} of {progress.total}
                    </span>
                    <span className="text-slate-500">
                      {progress.done} done{progress.failed ? ` · ${progress.failed} failed` : ""}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-indigo-500 transition-all duration-500"
                      style={{ width: `${Math.round(((progress.done + progress.failed) / progress.total) * 100)}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    This runs on the server — you can leave this page and come back to it from Drafts.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {slides.map((s, i) => (
                  <div key={i} className="rounded-xl bg-slate-950/60 border border-slate-800 p-3 flex gap-3" data-testid={`slide-${i}`}>
                    <div className="w-20 h-20 rounded-lg bg-slate-900 overflow-hidden flex items-center justify-center shrink-0 relative">
                      {imageSrc(s) ? (
                        <img src={imageSrc(s)} alt="" className="w-full h-full object-cover" />
                      ) : s.image_status === "generating" ? (
                        <Loader2 className="w-5 h-5 animate-spin text-indigo-300" />
                      ) : s.image_status === "failed" ? (
                        <span className="text-rose-400 text-[10px] text-center px-1">failed</span>
                      ) : (
                        <span className="text-slate-700 text-xs">{i + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <input value={s.heading} onChange={(e) => updateSlide(i, "heading", e.target.value)} placeholder="Heading" className="w-full bg-transparent text-sm font-semibold text-slate-100 focus:outline-none" />
                      <textarea value={s.caption} onChange={(e) => updateSlide(i, "caption", e.target.value)} rows={2} placeholder="Slide text" className="w-full bg-transparent text-xs text-slate-400 focus:outline-none resize-none" />
                      <button data-testid={`gen-slide-image-${i}`} onClick={() => genSlideImage(i)} disabled={slideBusy === i}
                        className="text-[11px] font-semibold text-indigo-300 hover:text-indigo-200 flex items-center gap-1 disabled:opacity-60">
                        {slideBusy === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {imageSrc(s) ? "Regenerate image" : "Generate image"}
                      </button>
                      {s.image_error && (
                        <p className="text-[10px] text-rose-400/80 break-words">{s.image_error}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button data-testid="save-draft-btn" onClick={saveDraft} disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold transition-colors disabled:opacity-60">
              <Save className="w-4 h-4" /> Save draft
            </button>
            <button data-testid="submit-approval-btn" onClick={submitApproval} disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 shadow-[0_0_24px_rgba(16,185,129,0.3)]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for approval
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-6">
            <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wider flex items-center gap-1.5"><FmtIcon className="w-3.5 h-3.5" /> Live preview</p>
            <motion.div layout className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden shadow-2xl" data-testid="composer-preview">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-sky-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-100">{accountIds.length ? accounts.find((a) => a.id === accountIds[0])?.display_name : "Your Brand"}</p>
                  <p className="text-xs text-slate-500">{platforms.map((p) => PLATFORM_META[p]?.label).join(" · ") || "Preview"}</p>
                </div>
              </div>
              {fmt === "carousel" && slides.length ? (
                <div className="flex overflow-x-auto snap-x">
                  {slides.map((s, i) => (
                    <div key={i} className="w-full shrink-0 snap-center aspect-square bg-slate-950 relative flex items-center justify-center">
                      {imageSrc(s) ? <img src={imageSrc(s)} alt="" className="w-full h-full object-cover" /> : <span className="text-slate-700 text-sm">Slide {i + 1}</span>}
                      {s.heading && <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent"><p className="text-sm font-semibold text-white">{s.heading}</p></div>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="aspect-square bg-slate-950 flex items-center justify-center">
                  {imageB64 ? <img src={`data:image/png;base64,${imageB64}`} alt="preview" className="w-full h-full object-cover" /> : (
                    <div className="text-slate-700 flex flex-col items-center gap-2"><ImageIcon className="w-10 h-10" /><span className="text-xs">No image yet</span></div>
                  )}
                </div>
              )}
              <div className="p-4">
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{caption || "Your caption preview will show here."}</p>
                {hashtags.length > 0 && <p className="text-xs text-indigo-400 mt-2 font-mono">{hashtags.map((h) => `#${h}`).join(" ")}</p>}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};
